import { FC, useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Accordion } from '@/ui'
import { FinancialAccountWithAdminInfo } from '@/reducers/admin/financialAccountsReducer'
import { TransactionCategory } from '@/features/reports/reports.slice'
import { LedgerAccount, GeneralLedgerLineItem } from '../types'
import { createGeneralLedgerViewModel } from '../service'
import { GeneralLedgerRow } from './GeneralLedgerRow'

export interface GeneralLedgerProps {
  generalLedgerItems: LedgerAccount[]
  financialAccountsById: {
    [key: string]: FinancialAccountWithAdminInfo
  }
  transactionCategoriesById: {
    [key: string]: TransactionCategory
  }
  accountFilter: string | undefined
  statementFilter: string | undefined
  sortBy: string
}

export interface GeneralLedgerAccount {
  name: string
  type: 'transactionCategory' | 'financialAccount'
  id: number
  key: string
  netTotalInCents: number
  balanceBroughtDownDetails: {
    sumDebits: number
    sumCredits: number
  }
  lineItems: GeneralLedgerLineItem[]
  totalDebitsInCents: number
  totalCreditsInCents: number
  accountFilter?: string
  statementFilter?: string
  sortBy?: string
  accountType?: string | null
  financialStatement?: string | null
}

export interface GeneralLedgerViewModel {
  accounts: GeneralLedgerAccount[]
}

export const NUM_ITEMS_IN_COLLAPSED_STATE = 10

export const GeneralLedger: FC<GeneralLedgerProps> = ({
  generalLedgerItems,
  transactionCategoriesById,
  financialAccountsById,
  accountFilter,
  statementFilter,
  sortBy,
}) => {
  const [searchParams] = useSearchParams()
  const listItemRef = useRef<HTMLDivElement | null>(null)
  // Manage open accordion values (string-based per ShadCN API)
  const [openValues, setOpenValues] = useState<string[]>([])

  const [openAccordionIndexes, setOpenAccordionIndexes] = useState(
    new Set<number>()
  )
  const [scrolledToRef, setScrolledToRef] = useState(false)

  const itemToScrollTo = useMemo(() => {
    if (searchParams.get('transactionCategoryId')) {
      return `category-${searchParams.get('transactionCategoryId')}`
    }
    if (searchParams.get('financialAccountId')) {
      return `financial-${searchParams.get('financialAccountId')}`
    }
    return null
  }, [searchParams])

  // Transform journalEntries into an easier model to work with
  const viewModel = useMemo(
    () =>
      createGeneralLedgerViewModel({
        generalLedgerItems,
        financialAccountsById,
        transactionCategoriesById,
        accountFilter,
        statementFilter,
        sortBy,
      }),
    [
      generalLedgerItems,
      financialAccountsById,
      transactionCategoriesById,
      accountFilter,
      statementFilter,
      sortBy,
    ]
  )

  const toggleAccordionValue = useCallback(
    (index: number) => {
      const clonedSet = new Set(openAccordionIndexes)

      if (clonedSet.has(index)) {
        clonedSet.delete(index)
      } else {
        clonedSet.add(index)
      }

      setOpenAccordionIndexes(clonedSet)
    },
    [openAccordionIndexes]
  )

  /*
    If this page is routed to with the financialAccountId or transactionCategoryId
    searchParams, scroll to the account with that id and automatically open it.
    This sets the scrolledToRef state to true so that it only scrolls once and not
    every time the viewModel updates (ex: on filters or sorts). 
  */
  useEffect(() => {
    if (itemToScrollTo && listItemRef.current && viewModel && !scrolledToRef) {
      listItemRef.current.scrollIntoView({ behavior: 'smooth' })
      const indexOfListItemRef = viewModel.accounts.findIndex(
        (account) => account.key === itemToScrollTo
      )
      if (indexOfListItemRef !== -1) {
        toggleAccordionValue(indexOfListItemRef)
      }
      setScrolledToRef(true)
    }
  }, [scrolledToRef, itemToScrollTo, viewModel, toggleAccordionValue])

  return (
    <Accordion
      type="multiple"
      value={openValues}
      onValueChange={(newValues) => setOpenValues(newValues)}
      className="w-full border-none"
    >
      {viewModel.accounts.map((account, i) => (
        <Accordion.Item key={account.key} value={`item-${i}`}>
          <GeneralLedgerRow
            account={account}
            active={openValues.includes(`item-${i}`)}
            index={i}
            onRowClick={toggleAccordionValue}
            forwardedRef={account.key === itemToScrollTo ? listItemRef : null}
          />
        </Accordion.Item>
      ))}
    </Accordion>
  )
}
